import { Box, makeStyles } from "@material-ui/core";
import { FormattedMessage, useIntl } from "react-intl";
import React, { useState } from "react";
import { FormikHelpers, useFormik } from "formik";

import { InputButton } from "../../../components/input-button/InputButton";
import { InputPin } from "../../../components/input-pin/InputPin";
import { SignInStateAttributes } from "../SignInScreen";
import { CheckPinRequestAttributes } from "../../../attributes/user-attributes";
import { UserService } from "../../../services/UserService";
import {
  remoteAuthUserRequest,
  useAuthenticationContext,
} from "../../../context/UserContext";
import User from "../../../models/User";
import { Overlay } from "../../../components/overlay/Overlay";
import { PIN_MAX_SIZE } from "../../../settings/constants";
import ConnectionLog from "../../../models/ConnectionLog";
import { deviceInfo } from "../../../common/Hooks";

interface CheckPinProps {
  pinLength: number;
  log: ConnectionLog | null;

  data: SignInStateAttributes;
  callback: () => void;
}

export const useStyles = makeStyles(
  (theme) => ({
    contentInner: {
      margin: "0 auto",
      width: "60%",
    },
  }),
  {
    name: "confirm-pin-style",
    flip: null,
  }
);

export const CheckPin = (props: CheckPinProps) => {
  const classes = useStyles(props);
  const intl = useIntl();
  // Props
  const { pinLength, data, callback, log } = props;
  // State
  const [pin, setPin] = useState("");
  // Context
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [user, token, authDispatch] = useAuthenticationContext();

  const handlePinChange = (pin: string) => {
    setPin(pin);
    formik.values.pin_code = pin;
  };

  const formik = useFormik({
    initialValues: {
      phone_number: data.phone,
      pin_code: "",
    },
    onSubmit: async (
      values: CheckPinRequestAttributes,
      { setErrors, setSubmitting }: FormikHelpers<any>
    ) => {
      try {
        const deviceInfos = deviceInfo();
        const requestData = {
          device_name: deviceInfos.device_name,
          device_model: deviceInfos.device_model,
          ...values,
        };
        const response = await UserService.checkPin(requestData);

        if (response.isSuccess) {
          switch (response.data.code) {
            case 150: // Validation error
              setErrors(JSON.parse(JSON.stringify(response.data.data)));
              setSubmitting(false);
              setPin("");
              break;
            case 100: // Ok
              // Get token and get user information
              let token = JSON.parse(JSON.stringify(response.data.data));
              // Get user info
              let user: User = await remoteAuthUserRequest(
                token.access_token,
                authDispatch
              );
              setPin("");
              setSubmitting(false);

              if (user !== null) {
                callback();
              }
              break;
            default:
              setErrors({ unknown: response.data.message });
              setSubmitting(false);
              setPin("");
              break;
          }
        } else {
          setPin("");
          setSubmitting(false);
          setErrors({
            unknown: intl.formatMessage({ id: "error.unknownError" }),
          });
        }
      } catch (err) {
        setPin("");
        setSubmitting(false);
        setErrors({
          unknown: intl.formatMessage({ id: "error.unknownError" }),
        });
      }
    },
  });

  const renderErrors = () => {
    if (formik.errors.unknown) {
      return formik.errors.unknown as string;
    }

    return "";
  };

  const handleSubmit = () => {
    if (pin.length === pinLength) {
      formik.handleSubmit();
    }
  };

  return (
    <>
      <Box
        marginBottom={1}
        component="span"
        className="title"
        textAlign="center"
      >
        <FormattedMessage
          id="sharedScreen.enterPin"
          values={{ size: PIN_MAX_SIZE }}
        />
      </Box>
      <Box component="div" display="flex" justifyContent="center">
        {log !== null ? (
          <Box
            component="span"
            className="subTitle"
            width="80%"
            textAlign="center"
          >
            <FormattedMessage
              id={log.displayLastConnection().message}
              values={log.displayLastConnection().values}
            />
          </Box>
        ) : (
          <></>
        )}
      </Box>

      <Box component="div" mt={6}>
        <Box className={classes.contentInner} component="div">
          <InputPin
            pinLength={pinLength}
            pin={pin}
            onPinChange={handlePinChange}
          />
          {formik.errors ? (
            <Box
              mb={2}
              component="div"
              className="errorWrapper"
              textAlign="center"
            >
              {renderErrors()}
            </Box>
          ) : (
            <></>
          )}

          <Box marginTop={3}>
            <InputButton
              color="secondary"
              fullWidth
              size="medium"
              variant="contained"
              onClick={(_) => handleSubmit()}
            >
              <FormattedMessage id="sharedScreen.continue" />
            </InputButton>
          </Box>
        </Box>
      </Box>

      <Overlay loading={formik.isSubmitting} />
    </>
  );
};
